import {
  ApiError,
  activateEvent,
  confirmEvent,
  listEvents,
  listLocations,
} from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { EventResponse } from '@jq4race/shared';
import { Button, Card, CardContent, Tabs, TabsContent, TabsList, TabsTrigger } from '@jq4race/ui';
import { Link } from '@tanstack/react-router';
import { CheckCircle2, Plus, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { AccessDeniedState } from '../../../../shared/errors/AccessDeniedState';
import { isForbiddenError } from '../../../../shared/errors/isForbiddenError';
import { RestrictedMeta } from '../../../../shared/errors/RestrictedMeta';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { useAutoDismissFeedback } from '../../../../shared/hooks/useAutoDismissFeedback';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { EventsTable } from '../components/EventsTable';
import { EventTemplatesSection } from '../components/EventTemplatesSection';

export function EventsPage() {
  const { t } = useTranslation('events');
  const [events, setEvents] = useState<EventResponse[]>([]);
  const [locationNames, setLocationNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when the last load failed with a 403 (confirmed production incident:
  // a role change mid-session leaves the frontend token stale, and the
  // backend correctly rejects the request). Drives two things: (1) the
  // events table area shows AccessDeniedState instead of the "no events"
  // empty state, and (2) the error banner is exempted from auto-dismiss —
  // see useAutoDismissFeedback.
  const [accessDenied, setAccessDenied] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [transitioningId, setTransitioningId] = useState<string | null>(null);
  const [tab, setTab] = useState<'events' | 'templates'>('events');

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAccessDenied(false);
    try {
      const [eventsRes, locationsRes] = await Promise.all([listEvents(), listLocations()]);
      setEvents(eventsRes.events);
      setLocationNames(Object.fromEntries(locationsRes.locations.map((loc) => [loc.id, loc.name])));
    } catch (e) {
      setAccessDenied(isForbiddenError(e));
      setError(translateApiError(e, t, { fallback: t('eventsPage.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useAutoDismissFeedback({
    error,
    success,
    isErrorPersistent: accessDenied,
    onDismissError: () => setError(null),
    onDismissSuccess: () => setSuccess(null),
  });

  async function handleConfirm(event: EventResponse) {
    setTransitioningId(event.id);
    try {
      await confirmEvent(event.id);
      setSuccess(t('eventsPage.confirmedSuccess', { name: event.name }));
      void reload();
    } catch (e) {
      setError(
        e instanceof ApiError
          ? translateApiError(e, t, { fallback: t('eventsPage.confirmError') })
          : t('eventsPage.confirmError'),
      );
    } finally {
      setTransitioningId(null);
    }
  }

  async function handleActivate(event: EventResponse) {
    setTransitioningId(event.id);
    try {
      await activateEvent(event.id);
      setSuccess(t('eventsPage.activatedSuccess', { name: event.name }));
      void reload();
    } catch (e) {
      setError(
        e instanceof ApiError
          ? translateApiError(e, t, { fallback: t('eventsPage.activateError') })
          : t('eventsPage.activateError'),
      );
    } finally {
      setTransitioningId(null);
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('eventsPage.eyebrow')}
        title={t('eventsPage.title')}
        description={t('eventsPage.description')}
        meta={
          <p className="text-[13px] text-jq-graphite">
            <RestrictedMeta loading={loading && events.length === 0} accessDenied={accessDenied}>
              <span className="font-medium tabular-nums text-jq-charcoal">{events.length}</span>{' '}
              {t('eventsPage.count', { count: events.length })}
            </RestrictedMeta>
          </p>
        }
        actions={
          <Link to="/dashboard/events/new">
            <Button size="sm">
              <Plus className="h-4 w-4" strokeWidth={1.75} />
              {t('eventsPage.newEvent')}
            </Button>
          </Link>
        }
      />

      <FeedbackBanner tone="error" message={error} onDismiss={() => setError(null)} />
      <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          <TabsTrigger value="events">{t('eventsPage.tabs.events')}</TabsTrigger>
          <TabsTrigger value="templates">{t('eventsPage.tabs.templates')}</TabsTrigger>
        </TabsList>

        <TabsContent value="events">
          <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
            <CardContent className="p-6 sm:p-8">
              {accessDenied ? (
                // The specific reason (backend message) is already shown in
                // the persistent error banner above — the panel itself only
                // needs the generic "access denied" copy so the two don't
                // repeat the same sentence.
                <AccessDeniedState />
              ) : (
                <EventsTable
                  events={events}
                  locationNames={locationNames}
                  loading={loading}
                  transitioningId={transitioningId}
                  onConfirm={handleConfirm}
                  onActivate={handleActivate}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates">
          <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
            <CardContent className="p-6 sm:p-8">
              <EventTemplatesSection />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Feedback banner ────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('events');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
